import type { App } from './App';
import type { BgStyle, BoostZoneDef, BubbleDef, ItemKind, ItemZoneDef, MapDefinition, PegDef, PopperDef, RotorDef, WallDef } from '../types';
import { MAPS } from '../core/maps';
import { deleteCustomMap, getCustomMaps, importCustomMaps, saveCustomMap } from '../storage/customMaps';
import { TRACKS } from '../audio/tracks';

const BG_STYLES: { id: BgStyle; label: string; icon: string }[] = [
  { id: 'space',   label: '우주',   icon: '🌌' },
  { id: 'arcade',  label: '아케이드', icon: '🕹️' },
  { id: 'desert',  label: '사막',   icon: '🏜️' },
  { id: 'dungeon', label: '던전',   icon: '🏰' },
  { id: 'pastel',  label: '파스텔', icon: '🌸' },
  { id: 'frost',   label: '설원',   icon: '❄️' },
];

type EditKind = 'wall' | 'peg' | 'rotor' | 'item' | 'popper' | 'bubble' | 'boost';
type DragMode = 'move' | 'resize' | 'rotate';
type Selection =
  | { kind: 'wall'; index: number }
  | { kind: 'peg'; index: number }
  | { kind: 'rotor'; index: number }
  | { kind: 'item'; index: number }
  | { kind: 'popper'; index: number }
  | { kind: 'bubble'; index: number }
  | { kind: 'boost'; index: number };

const ITEM_LABELS: Record<ItemKind, string> = {
  boost: '가속',
  slow: '감속',
  chaos: '혼란',
  swap: '선두 추격',
  shield: '방패',
  teleport: '순간이동',
  vortex: '소용돌이',
  anchor: '닻',
};

export class CustomMapModal {
  private el: HTMLElement;
  private app: App;
  private draft: MapDefinition;
  private selected: Selection | null = null;
  private tool: EditKind = 'peg';
  private dragging = false;
  private dragMode: DragMode = 'move';
  private highlightedSelection: Selection | null = null;
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;

  constructor(container: HTMLElement, app: App) {
    this.app = app;
    this.el = document.createElement('div');
    this.el.className = 'custom-map-wrap';
    container.appendChild(this.el);
    this.draft = this.cloneAsCustom(MAPS[0]);
    this.render();
  }

  private render() {
    const customMaps = getCustomMaps();
    this.el.innerHTML = `
      <div class="custom-map-modal">
        <div class="modal-header">
          <h3>맵 커스텀</h3>
          <button class="btn-icon-sm" id="btn-close-custom">✕</button>
        </div>
        <div class="custom-map-layout">
          <aside class="custom-map-panel">
            <label class="form-label">기본 맵으로 시작</label>
            <div class="inline-actions">
              <select class="select-class" id="base-map">
                ${MAPS.map((m) => `<option value="${m.id}">${m.name}</option>`).join('')}
              </select>
              <button class="btn-sm" id="btn-load-base">새 맵으로 시작</button>
            </div>

            <label class="form-label">맵 이름</label>
            <input class="form-input" id="custom-map-name" value="${this.escape(this.draft.name)}" />

            <label class="form-label">배경 음악</label>
            <select class="select-class" id="custom-bgm-track">
              <option value="">없음 (무음)</option>
              ${TRACKS.map((t) => `<option value="${t.id}" ${this.draft.bgmTrack === t.id ? 'selected' : ''}>${t.name}</option>`).join('')}
            </select>

            <label class="form-label">배경 스타일</label>
            <div class="bg-style-grid" id="bg-style-grid">
              ${BG_STYLES.map((s) => `
                <button class="bg-style-btn ${this.draft.bgStyle === s.id ? 'active' : ''}" data-bg-style="${s.id}" title="${s.label}">
                  <span>${s.icon}</span>
                  <small>${s.label}</small>
                </button>
              `).join('')}
            </div>

            <div class="custom-editor-hint">
              <strong>편집 방법</strong>
              <span>선택한 요소는 드래그해서 옮겨요. 노란 핸들로 길이나 크기를, 보라 핸들로 회전을 조절해요.</span>
            </div>

            <label class="form-label">추가할 요소</label>
            <div class="tool-grid">
              ${(['wall', 'peg', 'rotor', 'item', 'popper', 'bubble', 'boost'] as EditKind[]).map((kind) => `
                <button class="tool-btn ${this.tool === kind ? 'active' : ''}" data-tool="${kind}">
                  <span>${this.toolIcon(kind)}</span>
                  <strong>${this.toolLabel(kind)}</strong>
                </button>
              `).join('')}
            </div>
            <div class="custom-object-actions">
              <button class="btn-primary-sm" id="btn-add-object">현재 화면에 추가</button>
              <button class="btn-sm" id="btn-duplicate-object" ${this.selected ? '' : 'disabled'}>복제</button>
              <button class="btn-sm btn-danger" id="btn-delete-object" ${this.selected ? '' : 'disabled'}>삭제</button>
            </div>

            <div class="selection-box" id="selection-box">
              ${this.renderSelectionEditor()}
            </div>

            <div class="custom-map-actions">
              <button class="btn-primary-sm" id="btn-save-map">저장</button>
              <button class="btn-sm" id="btn-export-map">내보내기</button>
              <label class="btn-sm btn-import-map">
                불러오기
                <input type="file" id="map-import-file" accept=".json,application/json" hidden />
              </label>
            </div>

            ${customMaps.length > 0 ? `
              <label class="form-label">저장된 커스텀 맵</label>
              <div class="custom-map-list">
                ${customMaps.map((m) => `
                  <div class="custom-map-list-item">
                    <span class="custom-map-name">${this.escape(m.name)}</span>
                    <div class="custom-map-item-actions">
                      <button class="btn-sm" data-load-custom="${m.id}">편집</button>
                      <button class="btn-sm btn-danger" data-delete-custom="${m.id}">삭제</button>
                    </div>
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </aside>
          <main class="custom-map-stage">
            <div class="custom-map-stagebar">
              <span>마우스 드래그: 이동</span>
              <span>노란 핸들: 길이/크기</span>
              <span>보라 핸들: 회전</span>
              <span>Delete: 삭제</span>
            </div>
            <div class="custom-map-scroll">
              <canvas id="custom-map-canvas" width="${this.draft.worldWidth}" height="${this.draft.worldHeight}"></canvas>
            </div>
          </main>
        </div>
      </div>
    `;

    this.canvas = this.el.querySelector<HTMLCanvasElement>('#custom-map-canvas')!;
    this.canvas.width = this.draft.worldWidth;
    this.canvas.height = this.draft.worldHeight;
    this.ctx = this.canvas.getContext('2d')!;
    this.bindEvents();
    this.draw();
  }

  private bindEvents() {
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('keydown', this.handleKeyDown);
    this.el.querySelector('#btn-close-custom')?.addEventListener('click', () => this.close());
    this.el.querySelector('#btn-load-base')?.addEventListener('click', () => {
      const id = this.el.querySelector<HTMLSelectElement>('#base-map')?.value;
      const base = MAPS.find((m) => m.id === id) ?? MAPS[0];
      this.draft = this.cloneAsCustom(base);
      this.selected = null;
      this.render();
      this.el.querySelector<HTMLInputElement>('#custom-map-name')?.focus();
    });
    this.el.querySelector('#custom-map-name')?.addEventListener('input', (e) => {
      this.draft.name = (e.target as HTMLInputElement).value.trim() || '커스텀 맵';
    });
    this.el.querySelector('#custom-bgm-track')?.addEventListener('change', (e) => {
      const val = (e.target as HTMLSelectElement).value;
      this.draft.bgmTrack = val || undefined;
    });
    this.el.querySelectorAll<HTMLButtonElement>('[data-bg-style]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const style = btn.dataset.bgStyle as BgStyle;
        this.draft.bgStyle = style;
        this.el.querySelectorAll('[data-bg-style]').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });
    this.el.querySelectorAll<HTMLButtonElement>('[data-tool]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.tool = btn.dataset.tool as EditKind;
        this.render();
      });
    });
    this.el.querySelector('#btn-add-object')?.addEventListener('click', () => {
      this.addObject(this.tool);
      this.draw();
      this.refreshSelectionBox();
      this.scrollToSelection();
    });
    this.el.querySelector('#btn-duplicate-object')?.addEventListener('click', () => {
      this.duplicateSelection();
      this.draw();
      this.refreshSelectionBox();
      this.scrollToSelection();
    });
    this.el.querySelector('#btn-delete-object')?.addEventListener('click', () => {
      this.deleteSelection();
      this.render();
    });
    this.el.querySelector('#btn-save-map')?.addEventListener('click', () => {
      this.draft.name = this.el.querySelector<HTMLInputElement>('#custom-map-name')?.value.trim() || this.draft.name;
      saveCustomMap(this.normalizeDraft(this.draft));
      this.render();
    });
    this.el.querySelector('#btn-export-map')?.addEventListener('click', () => this.exportMap());
    this.el.querySelector<HTMLInputElement>('#map-import-file')?.addEventListener('change', (e) => {
      void this.importMap((e.target as HTMLInputElement).files?.[0]);
    });
    this.el.querySelectorAll<HTMLButtonElement>('[data-load-custom]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const map = getCustomMaps().find((m) => m.id === btn.dataset.loadCustom);
        if (!map) return;
        this.draft = this.cloneMap(map);
        this.selected = null;
        this.render();
        const nameInput = this.el.querySelector<HTMLInputElement>('#custom-map-name');
        if (nameInput) { nameInput.focus(); nameInput.select(); }
      });
    });
    this.el.querySelectorAll<HTMLButtonElement>('[data-delete-custom]').forEach((btn) => {
      btn.addEventListener('click', () => {
        deleteCustomMap(btn.dataset.deleteCustom!);
        this.render();
      });
    });
    this.bindSelectionEditor();
    this.canvas.addEventListener('pointerdown', (e) => this.handlePointerDown(e));
    this.canvas.addEventListener('pointermove', (e) => this.handlePointerMove(e));
    window.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('keydown', this.handleKeyDown);
  }

  private bindSelectionEditor() {
    const itemKind = this.el.querySelector<HTMLSelectElement>('#selected-item-kind');
    itemKind?.addEventListener('change', () => {
      if (!this.selected || this.selected.kind !== 'item') return;
      this.draft.itemZones![this.selected.index].kind = itemKind.value as ItemKind;
      this.draw();
    });
    this.el.querySelectorAll<HTMLInputElement>('[data-edit-field]').forEach((input) => {
      input.addEventListener('input', () => {
        const target = this.getSelectedObject();
        if (!target) return;
        const field = input.dataset.editField!;
        const value = Number(input.value);
        if (!Number.isFinite(value)) return;
        (target as unknown as Record<string, number>)[field] = value;
        this.draw();
      });
    });
  }

  private handlePointerDown(e: PointerEvent) {
    const p = this.eventToWorld(e);
    const handle = this.pickSelectionHandle(p.x, p.y);
    if (handle) {
      this.dragMode = handle;
      this.dragging = true;
      this.canvas.setPointerCapture(e.pointerId);
      return;
    }
    this.selected = this.pickObject(p.x, p.y);
    if (!this.selected && e.detail >= 2) {
      this.addObject(this.tool, p.x, p.y);
      this.scrollToSelection(false);
    }
    this.dragMode = 'move';
    this.dragging = Boolean(this.selected);
    this.canvas.setPointerCapture(e.pointerId);
    this.refreshSelectionBox();
    this.draw();
  }

  private handlePointerMove(e: PointerEvent) {
    if (!this.dragging || !this.selected) return;
    const p = this.eventToWorld(e);
    const target = this.getSelectedObject();
    if (!target) return;
    if (this.dragMode === 'rotate' && this.canRotateSelection()) {
      this.applyRotationDrag(target, p.x, p.y);
      this.refreshSelectionBox(false);
      this.draw();
      return;
    }
    if (this.dragMode === 'resize') {
      this.applyResizeDrag(target, p.x, p.y);
      this.refreshSelectionBox(false);
      this.draw();
      return;
    }
    target.x = this.clamp(p.x, 20, this.draft.worldWidth - 20);
    target.y = this.clamp(p.y, 40, this.draft.goalY - 20);
    this.refreshSelectionBox(false);
    this.draw();
  }

  private readonly handlePointerUp = () => {
    this.dragging = false;
    this.dragMode = 'move';
  };

  private draw() {
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const sx = w / this.draft.worldWidth;
    const sy = h / this.draft.worldHeight;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#07111f';
    ctx.fillRect(0, 0, w, h);
    ctx.save();
    ctx.scale(sx, sy);

    ctx.strokeStyle = 'rgba(94,234,212,0.35)';
    ctx.lineWidth = 1 / Math.min(sx, sy);
    for (let y = 0; y <= this.draft.worldHeight; y += 400) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(this.draft.worldWidth, y); ctx.stroke();
    }

    this.draft.walls.forEach((wall, i) => this.drawWall(ctx, wall, this.isSelected('wall', i)));
    this.draft.pegs.forEach((peg, i) => this.drawCircle(ctx, peg, '#5EEAD4', this.isSelected('peg', i)));
    this.draft.rotors?.forEach((r, i) => this.drawRotor(ctx, r, this.isSelected('rotor', i)));
    this.draft.boostZones?.forEach((b, i) => this.drawBoost(ctx, b, this.isSelected('boost', i)));
    this.draft.itemZones?.forEach((item, i) => this.drawItem(ctx, item, this.isSelected('item', i)));
    this.draft.poppers?.forEach((p, i) => this.drawCircle(ctx, p, '#FDBA74', this.isSelected('popper', i), '!'));
    this.draft.bubbles?.forEach((b, i) => this.drawCircle(ctx, b, '#A78BFA', this.isSelected('bubble', i)));

    ctx.strokeStyle = '#FB7185';
    ctx.lineWidth = 4 / Math.min(sx, sy);
    ctx.setLineDash([16, 10]);
    ctx.beginPath(); ctx.moveTo(20, this.draft.goalY); ctx.lineTo(this.draft.worldWidth - 20, this.draft.goalY); ctx.stroke();
    this.drawSelectionLabel(ctx);
    this.drawSelectionHandles(ctx);
    ctx.restore();
  }

  private drawWall(ctx: CanvasRenderingContext2D, wall: WallDef, selected: boolean) {
    ctx.save();
    ctx.translate(wall.x, wall.y);
    ctx.rotate(wall.angle ?? 0);
    ctx.fillStyle = selected ? '#FBBF24' : '#5EEAD4';
    if (selected || this.isHighlighted('wall', this.draft.walls.indexOf(wall))) {
      ctx.shadowColor = '#FBBF24';
      ctx.shadowBlur = 18;
    }
    ctx.fillRect(-wall.width / 2, -wall.height / 2, wall.width, wall.height);
    ctx.restore();
  }

  private drawRotor(ctx: CanvasRenderingContext2D, rotor: RotorDef, selected: boolean) {
    ctx.save();
    ctx.translate(rotor.x, rotor.y);
    ctx.rotate(rotor.angle ?? 0);
    ctx.fillStyle = selected ? '#FBBF24' : '#7C3AED';
    ctx.strokeStyle = '#FBBF24';
    ctx.lineWidth = 3;
    ctx.fillRect(-rotor.width / 2, -rotor.height / 2, rotor.width, rotor.height);
    ctx.strokeRect(-rotor.width / 2, -rotor.height / 2, rotor.width, rotor.height);
    ctx.restore();
  }

  private drawCircle(ctx: CanvasRenderingContext2D, obj: { x: number; y: number; radius: number }, color: string, selected: boolean, text = '') {
    ctx.save();
    ctx.beginPath();
    ctx.arc(obj.x, obj.y, obj.radius, 0, Math.PI * 2);
    ctx.fillStyle = selected ? '#FBBF24' : 'rgba(8,12,18,0.88)';
    if (selected) {
      ctx.shadowColor = '#FBBF24';
      ctx.shadowBlur = 18;
    }
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = selected ? 5 : 3;
    ctx.stroke();
    if (text) {
      ctx.fillStyle = color;
      ctx.font = `900 ${obj.radius}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, obj.x, obj.y);
    }
    ctx.restore();
  }

  private drawItem(ctx: CanvasRenderingContext2D, item: ItemZoneDef, selected: boolean) {
    const color = selected ? '#FBBF24' : '#22D3EE';
    this.drawCircle(ctx, item, color, selected, this.itemGlyph(item.kind));
  }

  private drawBoost(ctx: CanvasRenderingContext2D, boost: BoostZoneDef, selected: boolean) {
    ctx.save();
    ctx.globalAlpha = selected ? 0.38 : 0.18;
    ctx.fillStyle = '#34D399';
    ctx.fillRect(boost.x - boost.width / 2, boost.y - boost.height / 2, boost.width, boost.height);
    ctx.strokeStyle = selected ? '#FBBF24' : '#34D399';
    ctx.lineWidth = selected ? 5 : 2;
    ctx.strokeRect(boost.x - boost.width / 2, boost.y - boost.height / 2, boost.width, boost.height);
    ctx.restore();
  }

  private renderSelectionEditor(): string {
    const target = this.getSelectedObject();
    if (!this.selected || !target) return '<p class="selection-empty">오브젝트를 선택하면 수치를 직접 조정할 수 있어요. 빈 곳을 더블클릭하면 지금 고른 도구를 바로 추가해요.</p>';
    const fields = ['x', 'y', ...this.extraFields(this.selected.kind)];
    return `
      <div class="selection-title">${this.toolLabel(this.selected.kind)} 선택됨</div>
      <p class="selection-help">캔버스의 노란 핸들을 끌어 길이/크기를 바꾸고, 보라 핸들을 끌어 회전하세요.</p>
      ${this.selected.kind === 'item' ? `
        <label class="form-label">아이템 종류</label>
        <select class="select-class" id="selected-item-kind">
          ${(Object.keys(ITEM_LABELS) as ItemKind[]).map((kind) => `<option value="${kind}" ${(target as ItemZoneDef).kind === kind ? 'selected' : ''}>${ITEM_LABELS[kind]}</option>`).join('')}
        </select>
      ` : ''}
      <div class="selection-grid">
        ${fields.map((field) => `
          <label>
            <span>${this.fieldLabel(field)}</span>
            <input class="form-input" data-edit-field="${field}" type="number" step="${field === 'angle' || field === 'angularVelocity' || field.startsWith('force') ? '0.1' : '1'}" value="${this.fieldValue(target, field)}" />
          </label>
        `).join('')}
      </div>
    `;
  }

  private refreshSelectionBox(rebind = true) {
    const box = this.el.querySelector('#selection-box');
    if (box) box.innerHTML = this.renderSelectionEditor();
    const del = this.el.querySelector<HTMLButtonElement>('#btn-delete-object');
    if (del) del.disabled = !this.selected;
    const dup = this.el.querySelector<HTMLButtonElement>('#btn-duplicate-object');
    if (dup) dup.disabled = !this.selected;
    if (rebind) this.bindSelectionEditor();
  }

  private addObject(kind: EditKind, x = this.visibleWorldCenter().x, y = this.visibleWorldCenter().y) {
    x = this.clamp(x, 40, this.draft.worldWidth - 40);
    y = this.clamp(y, 120, this.draft.goalY - 80);
    if (kind === 'wall') {
      this.draft.walls.push({ x, y, width: 160, height: 14, angle: 0, restitution: 0.12, friction: 0.02 });
      this.selected = { kind, index: this.draft.walls.length - 1 };
    } else if (kind === 'peg') {
      this.draft.pegs.push({ x, y, radius: 7, restitution: 0.36 });
      this.selected = { kind, index: this.draft.pegs.length - 1 };
    } else if (kind === 'rotor') {
      this.draft.rotors ??= [];
      this.draft.rotors.push({ x, y, width: 120, height: 10, angularVelocity: 1.5, restitution: 0.25 });
      this.selected = { kind, index: this.draft.rotors.length - 1 };
    } else if (kind === 'item') {
      this.draft.itemZones ??= [];
      this.draft.itemZones.push({ id: `item-${Date.now()}`, kind: 'boost', x, y, radius: 24 });
      this.selected = { kind, index: this.draft.itemZones.length - 1 };
    } else if (kind === 'popper') {
      this.draft.poppers ??= [];
      this.draft.poppers.push({ id: `pop-${Date.now()}`, x, y, radius: 20, force: 3.8 });
      this.selected = { kind, index: this.draft.poppers.length - 1 };
    } else if (kind === 'bubble') {
      this.draft.bubbles ??= [];
      this.draft.bubbles.push({ id: `bubble-${Date.now()}`, x, y, radius: 13, restitution: 0.9 });
      this.selected = { kind, index: this.draft.bubbles.length - 1 };
    } else {
      this.draft.boostZones ??= [];
      this.draft.boostZones.push({ x, y, width: 360, height: 100, forceY: 0.0045 });
      this.selected = { kind, index: this.draft.boostZones.length - 1 };
    }
    this.highlightSelection();
  }

  private duplicateSelection() {
    if (!this.selected) return;
    const array = this.getArrayForKind(this.selected.kind);
    const target = this.getSelectedObject();
    if (!array || !target) return;
    const clone = this.cloneMap(target) as typeof target;
    clone.x = this.clamp(clone.x + 24, 40, this.draft.worldWidth - 40);
    clone.y = this.clamp(clone.y + 44, 120, this.draft.goalY - 80);
    if ('id' in clone) clone.id = `${this.selected.kind}-${Date.now()}`;
    array.push(clone as never);
    this.selected = { kind: this.selected.kind, index: array.length - 1 } as Selection;
    this.highlightSelection();
  }

  private deleteSelection() {
    if (!this.selected) return;
    const arrays = this.getArrayForKind(this.selected.kind);
    arrays?.splice(this.selected.index, 1);
    this.selected = null;
  }

  private pickObject(x: number, y: number): Selection | null {
    const candidates: Array<{ sel: Selection; d: number }> = [];
    const add = (sel: Selection, ox: number, oy: number, threshold: number) => {
      const d = Math.hypot(x - ox, y - oy);
      if (d <= threshold) candidates.push({ sel, d });
    };
    this.draft.walls.forEach((o, i) => add({ kind: 'wall', index: i }, o.x, o.y, Math.max(24, o.height + 16)));
    this.draft.pegs.forEach((o, i) => add({ kind: 'peg', index: i }, o.x, o.y, o.radius + 18));
    this.draft.rotors?.forEach((o, i) => add({ kind: 'rotor', index: i }, o.x, o.y, Math.max(26, o.width / 2)));
    this.draft.itemZones?.forEach((o, i) => add({ kind: 'item', index: i }, o.x, o.y, o.radius + 18));
    this.draft.poppers?.forEach((o, i) => add({ kind: 'popper', index: i }, o.x, o.y, o.radius + 18));
    this.draft.bubbles?.forEach((o, i) => add({ kind: 'bubble', index: i }, o.x, o.y, o.radius + 18));
    this.draft.boostZones?.forEach((o, i) => add({ kind: 'boost', index: i }, o.x, o.y, Math.max(o.width, o.height) / 2));
    return candidates.sort((a, b) => a.d - b.d)[0]?.sel ?? null;
  }

  private getSelectedObject(): WallDef | PegDef | RotorDef | ItemZoneDef | PopperDef | BubbleDef | BoostZoneDef | null {
    if (!this.selected) return null;
    return this.getArrayForKind(this.selected.kind)?.[this.selected.index] ?? null;
  }

  private getArrayForKind(kind: EditKind) {
    if (kind === 'wall') return this.draft.walls;
    if (kind === 'peg') return this.draft.pegs;
    if (kind === 'rotor') return this.draft.rotors;
    if (kind === 'item') return this.draft.itemZones;
    if (kind === 'popper') return this.draft.poppers;
    if (kind === 'bubble') return this.draft.bubbles;
    return this.draft.boostZones;
  }

  private eventToWorld(e: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * this.draft.worldWidth,
      y: ((e.clientY - rect.top) / rect.height) * this.draft.worldHeight,
    };
  }

  private visibleWorldCenter() {
    const scroll = this.el.querySelector<HTMLElement>('.custom-map-scroll');
    if (!scroll) return { x: this.draft.worldWidth / 2, y: Math.min(this.draft.goalY - 120, 900) };
    const canvasRect = this.canvas.getBoundingClientRect();
    const scrollRect = scroll.getBoundingClientRect();
    const centerClientX = scrollRect.left + scroll.clientWidth / 2;
    const centerClientY = scrollRect.top + scroll.clientHeight / 2;
    return {
      x: ((centerClientX - canvasRect.left) / canvasRect.width) * this.draft.worldWidth,
      y: ((centerClientY - canvasRect.top) / canvasRect.height) * this.draft.worldHeight,
    };
  }

  private scrollToSelection(smooth = true) {
    const target = this.getSelectedObject();
    const scroll = this.el.querySelector<HTMLElement>('.custom-map-scroll');
    if (!target || !scroll) return;
    const scaleY = this.canvas.clientHeight / this.draft.worldHeight;
    const scaleX = this.canvas.clientWidth / this.draft.worldWidth;
    scroll.scrollTo({
      left: Math.max(0, target.x * scaleX - scroll.clientWidth / 2),
      top: Math.max(0, target.y * scaleY - scroll.clientHeight / 2),
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  private highlightSelection() {
    this.highlightedSelection = this.selected ? { ...this.selected } : null;
    window.setTimeout(() => {
      this.highlightedSelection = null;
      this.draw();
    }, 900);
  }

  private rotateSelection(deg: number) {
    const target = this.getSelectedObject();
    if (!target || !this.canRotateSelection()) return;
    const current = (target as WallDef | RotorDef).angle ?? 0;
    (target as WallDef | RotorDef).angle = current + (deg * Math.PI) / 180;
    this.refreshSelectionBox();
    this.draw();
  }

  private canRotateSelection() {
    return this.selected?.kind === 'wall' || this.selected?.kind === 'rotor';
  }

  private pickSelectionHandle(x: number, y: number): DragMode | null {
    if (!this.selected) return null;
    const handles = this.selectionHandles();
    const hit = (p: { x: number; y: number } | null) => p && Math.hypot(x - p.x, y - p.y) <= 20;
    if (hit(handles.rotate)) return 'rotate';
    if (hit(handles.resize)) return 'resize';
    return null;
  }

  private selectionHandles(): { resize: { x: number; y: number } | null; rotate: { x: number; y: number } | null } {
    const target = this.getSelectedObject();
    if (!this.selected || !target) return { resize: null, rotate: null };

    if (this.selected.kind === 'wall' || this.selected.kind === 'rotor') {
      const obj = target as WallDef | RotorDef;
      const angle = obj.angle ?? 0;
      return {
        resize: this.localToWorld(obj.x, obj.y, obj.width / 2 + 22, 0, angle),
        rotate: this.localToWorld(obj.x, obj.y, 0, -58, angle),
      };
    }

    if (this.selected.kind === 'boost') {
      const obj = target as BoostZoneDef;
      return {
        resize: { x: obj.x + obj.width / 2 + 14, y: obj.y + obj.height / 2 + 14 },
        rotate: null,
      };
    }

    if ('radius' in target) {
      return {
        resize: { x: target.x + target.radius + 18, y: target.y },
        rotate: null,
      };
    }

    return { resize: null, rotate: null };
  }

  private applyResizeDrag(target: WallDef | PegDef | RotorDef | ItemZoneDef | PopperDef | BubbleDef | BoostZoneDef, x: number, y: number) {
    if (!this.selected) return;
    if (this.selected.kind === 'wall' || this.selected.kind === 'rotor') {
      const obj = target as WallDef | RotorDef;
      const dx = x - obj.x;
      const dy = y - obj.y;
      obj.width = this.clamp(Math.hypot(dx, dy) * 2, 28, this.draft.worldWidth);
      obj.angle = Math.atan2(dy, dx);
      return;
    }
    if (this.selected.kind === 'boost') {
      const obj = target as BoostZoneDef;
      obj.width = this.clamp(Math.abs(x - obj.x) * 2, 40, this.draft.worldWidth);
      obj.height = this.clamp(Math.abs(y - obj.y) * 2, 30, 480);
      return;
    }
    if ('radius' in target) {
      target.radius = this.clamp(Math.hypot(x - target.x, y - target.y), 5, 72);
    }
  }

  private applyRotationDrag(target: WallDef | RotorDef | PegDef | ItemZoneDef | PopperDef | BubbleDef | BoostZoneDef, x: number, y: number) {
    if (!this.selected || !this.canRotateSelection()) return;
    (target as WallDef | RotorDef).angle = Math.atan2(y - target.y, x - target.x) + Math.PI / 2;
  }

  private localToWorld(x: number, y: number, localX: number, localY: number, angle: number) {
    return {
      x: x + localX * Math.cos(angle) - localY * Math.sin(angle),
      y: y + localX * Math.sin(angle) + localY * Math.cos(angle),
    };
  }

  private readonly handleKeyDown = (e: KeyboardEvent) => {
    if (!this.selected) return;
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement) return;
    const target = this.getSelectedObject();
    if (!target) return;
    const step = e.shiftKey ? 20 : 5;
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      this.deleteSelection();
      this.refreshSelectionBox();
      this.draw();
      return;
    }
    const moves: Record<string, [number, number]> = {
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    target.x = this.clamp(target.x + move[0], 20, this.draft.worldWidth - 20);
    target.y = this.clamp(target.y + move[1], 40, this.draft.goalY - 20);
    this.refreshSelectionBox(false);
    this.draw();
  };

  private exportMap() {
    const data = JSON.stringify(this.normalizeDraft(this.draft), null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${this.draft.name.replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private async importMap(file?: File) {
    if (!file) return;
    const text = await file.text();
    const parsed = JSON.parse(text);
    const maps = Array.isArray(parsed) ? parsed : [parsed];
    importCustomMaps(maps.map((m) => this.normalizeDraft(m)));
    this.draft = this.cloneMap(maps[0]);
    this.selected = null;
    this.render();
  }

  private normalizeDraft(map: MapDefinition): MapDefinition {
    return {
      ...this.cloneMap(map),
      id: map.id.startsWith('custom-') ? map.id : `custom-${Date.now()}`,
      thumbnail: map.thumbnail || '🛠',
      difficulty: map.difficulty || 4,
      itemZones: map.itemZones ?? [],
      rotors: map.rotors ?? [],
      poppers: map.poppers ?? [],
      bubbles: map.bubbles ?? [],
      boostZones: map.boostZones ?? [],
    };
  }

  private cloneAsCustom(map: MapDefinition): MapDefinition {
    return {
      ...this.cloneMap(map),
      id: `custom-${Date.now()}`,
      name: `${map.name} 커스텀`,
      thumbnail: '🛠',
    };
  }

  private cloneMap<T>(map: T): T {
    return JSON.parse(JSON.stringify(map)) as T;
  }

  private extraFields(kind: EditKind) {
    if (kind === 'wall') return ['width', 'height', 'angle'];
    if (kind === 'peg' || kind === 'item' || kind === 'popper' || kind === 'bubble') return ['radius'];
    if (kind === 'rotor') return ['width', 'height', 'angularVelocity', 'angle'];
    return ['width', 'height', 'forceY', 'forceX'];
  }

  private fieldLabel(field: string) {
    return {
      x: '가로 위치',
      y: '세로 위치',
      width: '너비',
      height: '두께/높이',
      angle: '각도(rad)',
      radius: '반지름',
      angularVelocity: '회전 속도',
      forceY: '아래 힘',
      forceX: '좌우 힘',
    }[field] ?? field;
  }

  private fieldValue(target: unknown, field: string) {
    const value = (target as Record<string, number>)[field] ?? 0;
    if (field === 'angle' || field === 'angularVelocity' || field.startsWith('force')) return Number(value).toFixed(2);
    return Math.round(value);
  }

  private toolLabel(kind: EditKind) {
    return {
      wall: '벽/루트',
      peg: '원형 핀',
      rotor: '회전바',
      item: '아이템',
      popper: '팝퍼',
      bubble: '버블',
      boost: '가속 구역',
    }[kind];
  }

  private toolIcon(kind: EditKind) {
    return {
      wall: '▰',
      peg: '○',
      rotor: '⟲',
      item: '⚡',
      popper: '!',
      bubble: '●',
      boost: '▭',
    }[kind];
  }

  private itemGlyph(kind: ItemKind) {
    return ({ boost: '⚡', slow: '🧊', chaos: '🌀', swap: '⇄', shield: '◇', teleport: '✦', vortex: '◎', anchor: '⬇' } as Record<ItemKind, string>)[kind];
  }

  private isSelected(kind: EditKind, index: number) {
    return this.selected?.kind === kind && this.selected.index === index;
  }

  private isHighlighted(kind: EditKind, index: number) {
    return this.highlightedSelection?.kind === kind && this.highlightedSelection.index === index;
  }

  private drawSelectionLabel(ctx: CanvasRenderingContext2D) {
    const target = this.getSelectedObject();
    if (!this.selected || !target) return;
    ctx.save();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(251,191,36,0.96)';
    ctx.strokeStyle = 'rgba(8,12,18,0.85)';
    ctx.lineWidth = 2;
    ctx.font = '900 18px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const text = this.toolLabel(this.selected.kind);
    const x = target.x;
    const y = Math.max(28, target.y - 42);
    const width = Math.max(84, ctx.measureText(text).width + 28);
    ctx.beginPath();
    ctx.roundRect(x - width / 2, y - 16, width, 32, 12);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#111827';
    ctx.fillText(text, x, y);
    ctx.restore();
  }

  private drawSelectionHandles(ctx: CanvasRenderingContext2D) {
    if (!this.selected) return;
    const target = this.getSelectedObject();
    if (!target) return;
    const handles = this.selectionHandles();

    ctx.save();
    ctx.setLineDash([]);
    ctx.lineWidth = 2;

    if (handles.rotate) {
      ctx.strokeStyle = 'rgba(167,139,250,0.72)';
      ctx.beginPath();
      ctx.moveTo(target.x, target.y);
      ctx.lineTo(handles.rotate.x, handles.rotate.y);
      ctx.stroke();
      this.drawHandle(ctx, handles.rotate.x, handles.rotate.y, '#A78BFA', '↻');
    }

    if (handles.resize) {
      ctx.strokeStyle = 'rgba(251,191,36,0.72)';
      ctx.beginPath();
      ctx.moveTo(target.x, target.y);
      ctx.lineTo(handles.resize.x, handles.resize.y);
      ctx.stroke();
      this.drawHandle(ctx, handles.resize.x, handles.resize.y, '#FBBF24', '↔');
    }

    ctx.restore();
  }

  private drawHandle(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, label: string) {
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = 12;
    ctx.fillStyle = '#08111f';
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x, y, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = color;
    ctx.font = '900 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x, y + 1);
    ctx.restore();
  }

  private clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value));
  }

  private escape(value: string) {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  private close() {
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('keydown', this.handleKeyDown);
    this.app.closeOverlay();
  }

  destroy() {
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('keydown', this.handleKeyDown);
    this.el.remove();
  }
}
